import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, PenLine, CreditCard, CheckCircle2, ShieldCheck, Sparkles, X } from "lucide-react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function DealRoom() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [deal, setDeal] = useState(null);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [signatureName, setSignatureName] = useState("");
    const [signing, setSigning] = useState(false);
    const [paying, setPaying] = useState(false);
    const [clause, setClause] = useState({ open: false, text: "", loading: false, data: null });
    const [wsStatus, setWsStatus] = useState("connecting"); // connecting | open | closed
    const [typingUser, setTypingUser] = useState("");
    // Smart Price Negotiator
    const [coachOffer, setCoachOffer] = useState("");
    const [coach, setCoach] = useState({ loading: false, data: null });
    const coachDebounceRef = useRef(null);
    const coachAbortRef = useRef(null);
    const chatBottomRef = useRef(null);
    const wsRef = useRef(null);
    const typingTimerRef = useRef(null);

    // WebSocket connection for real-time deal chat
    useEffect(() => {
        const token = localStorage.getItem("sr_token");
        if (!token || !id) return;
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const wsProto = backend.startsWith("https") ? "wss" : "ws";
        const host = backend.replace(/^https?:\/\//, "");
        const ws = new WebSocket(`${wsProto}://${host}/api/ws/deal/${id}?token=${encodeURIComponent(token)}`);
        wsRef.current = ws;
        ws.onopen = () => setWsStatus("open");
        ws.onclose = () => setWsStatus("closed");
        ws.onerror = () => setWsStatus("closed");
        ws.onmessage = (ev) => {
            try {
                const data = JSON.parse(ev.data);
                if (data.type === "message") {
                    setDeal(prev => prev ? { ...prev, messages: [...(prev.messages || []), data.message] } : prev);
                } else if (data.type === "typing") {
                    setTypingUser(data.user);
                    clearTimeout(typingTimerRef.current);
                    typingTimerRef.current = setTimeout(() => setTypingUser(""), 2000);
                }
            } catch (_) {}
        };
        return () => { try { ws.close(); } catch (_) {} };
    }, [id]);

    const explainClause = async (text) => {
        setClause({ open: true, text, loading: true, data: null });
        try {
            const { data } = await api.post("/ai/explain-clause", { clause_text: text });
            setClause({ open: true, text, loading: false, data });
        } catch (e) {
            setClause({ open: false, text: "", loading: false, data: null });
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "AI explain failed");
        }
    };

    const explainSelection = () => {
        const sel = typeof window !== "undefined" ? window.getSelection().toString().trim() : "";
        if (!sel || sel.length < 20) {
            toast.error("Select at least a clause (20+ chars) of the contract first");
            return;
        }
        explainClause(sel.slice(0, 1500));
    };

    const runCoach = (offerStr) => {
        clearTimeout(coachDebounceRef.current);
        const n = Number(offerStr);
        if (!n || !deal?.film_id) {
            setCoach({ loading: false, data: null });
            return;
        }
        coachDebounceRef.current = setTimeout(async () => {
            setCoach((c) => ({ ...c, loading: true }));
            try {
                const { data } = await api.post("/ai/negotiate", {
                    film_id: deal.film_id,
                    offer_usd: n,
                    actor: user?.role === "creator" ? "seller" : "buyer",
                });
                setCoach({ loading: false, data });
            } catch (_) {
                setCoach({ loading: false, data: null });
            }
        }, 600);
    };

    const load = async () => {
        try {
            const { data } = await api.get(`/deals/${id}`);
            setDeal(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    useEffect(() => {
        chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [deal?.messages?.length]);

    useEffect(() => {
        if (user?.name && !signatureName) setSignatureName(user.name);
    }, [user, signatureName]);

    const send = async (e) => {
        e.preventDefault();
        if (!msg.trim()) return;
        const ws = wsRef.current;
        if (ws && ws.readyState === 1) {
            ws.send(JSON.stringify({ type: "message", text: msg.trim() }));
            setMsg("");
            return;
        }
        // Fallback: REST POST (in case WS is closed)
        try {
            await api.post(`/deals/${id}/messages`, { message: msg.trim() });
            setMsg("");
            await load();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const onTyping = (v) => {
        setMsg(v);
        const ws = wsRef.current;
        if (ws && ws.readyState === 1) {
            try { ws.send(JSON.stringify({ type: "typing" })); } catch (_) {}
        }
    };

    const sign = async () => {
        if (!signatureName.trim()) {
            alert("Please type your legal name to sign.");
            return;
        }
        setSigning(true);
        try {
            await api.post("/contracts/sign", {
                deal_id: id,
                signature_name: signatureName.trim(),
                contract_type: "buyer_purchase",
            });
            await load();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSigning(false);
        }
    };

    const pay = async () => {
        setPaying(true);
        try {
            const { data } = await api.post("/payments/checkout", {
                deal_id: id,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            setPaying(false);
        }
    };

    if (err) {
        return (
            <div className="min-h-screen bg-ink-300">
                <Navbar />
                <div className="max-w-[900px] mx-auto px-6 py-24 text-center text-silver-400">
                    {err}.{" "}
                    <Link to="/catalog" className="text-platinum">
                        Back to catalog
                    </Link>
                </div>
            </div>
        );
    }
    if (!deal) {
        return (
            <div className="min-h-screen bg-ink-300">
                <Navbar />
                <div className="max-w-[900px] mx-auto px-6 py-24 text-silver-400 animate-pulse">
                    Opening deal room…
                </div>
            </div>
        );
    }

    const STEPS = [
        { k: "initiated", label: "Room Opened" },
        { k: "signed", label: "Contract Signed" },
        { k: "paid", label: "Escrow Paid" },
        { k: "transfer_complete", label: "Transfer Complete" },
    ];
    const stepIndex = Math.max(
        0,
        STEPS.findIndex((s) => s.k === deal.status),
    );

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <div className="max-w-[1500px] mx-auto px-4 md:px-10 py-6 md:py-10">
                {/* HEADER */}
                <div className="flex flex-wrap items-end justify-between gap-4 md:gap-6 mb-6 md:mb-8">
                    <div className="flex items-center gap-4 md:gap-5">
                        {deal.film_poster && (
                            <img
                                src={deal.film_poster}
                                alt={deal.film_title}
                                className="w-14 h-20 md:w-16 md:h-24 object-cover rounded-sm border border-white/10"
                            />
                        )}
                        <div>
                            <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-platinum mb-1">
                                Deal Room
                            </div>
                            <h1 className="font-display text-2xl md:text-4xl">
                                {deal.film_title}
                            </h1>
                            <div className="text-silver-400 text-xs md:text-sm">
                                Buyer: {deal.buyer_name} {deal.buyer_company && `· ${deal.buyer_company}`}
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-silver-400">
                            Consideration
                        </div>
                        <div className="font-display text-2xl md:text-3xl platinum-text">
                            ${Number(deal.price_usd).toLocaleString()}
                        </div>
                    </div>
                </div>

                {/* STEPPER */}
                <div className="glass p-4 md:p-5 rounded-sm mb-8 overflow-x-auto" data-testid="deal-stepper">
                    <div className="flex items-center justify-between min-w-[520px] md:min-w-0">
                        {STEPS.map((s, i) => (
                            <div key={s.k} className="flex-1 flex items-center gap-2 md:gap-3">
                                <div
                                    className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs shrink-0 ${
                                        i <= stepIndex
                                            ? "bg-platinum text-ink-300"
                                            : "border border-white/15 text-silver-500"
                                    }`}
                                >
                                    {i < stepIndex ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                                </div>
                                <div className="text-[10px] md:text-xs uppercase tracking-widest">
                                    <div
                                        className={
                                            i <= stepIndex ? "text-white" : "text-silver-500"
                                        }
                                    >
                                        {s.label}
                                    </div>
                                </div>
                                {i < STEPS.length - 1 && (
                                    <div
                                        className={`flex-1 h-px mx-2 md:mx-3 ${
                                            i < stepIndex ? "bg-platinum" : "bg-white/10"
                                        }`}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* SPLIT LAYOUT */}
                <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
                    {/* LEFT: contract */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="glass rounded-sm overflow-hidden flex flex-col"
                    >
                        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
                            <div className="flex items-center gap-2 text-silver-300 text-sm">
                                <ShieldCheck className="w-4 h-4 text-platinum" /> Exclusive Purchase Agreement
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={explainSelection}
                                    className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase text-platinum hover:text-white border border-platinum/40 hover:border-platinum px-2.5 py-1 rounded-sm"
                                    data-testid="explain-clause-btn"
                                    title="Select any clause in the contract text, then click here"
                                >
                                    <Sparkles className="w-3 h-3" /> Explain selected clause
                                </button>
                                <div className="text-xs text-silver-500 tracking-widest uppercase">
                                    Preview · v1
                                </div>
                            </div>
                        </div>
                        <pre
                            className="flex-1 px-6 py-5 text-[13px] whitespace-pre-wrap leading-relaxed text-silver-200 font-sans max-h-[520px] overflow-y-auto"
                            data-testid="contract-preview"
                        >
                            {deal.purchase_preview}
                        </pre>

                        {/* Smart Price Negotiator */}
                        {(user?.role === "buyer" || user?.role === "creator") && deal.status !== "transfer_complete" && (
                            <div className="mx-5 mb-4 border border-platinum/25 rounded-sm p-4 bg-platinum/[0.03]" data-testid="smart-negotiator">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2 text-platinum text-[11px] tracking-[0.3em] uppercase">
                                        <Sparkles className="w-3.5 h-3.5" />
                                        Smart Price Negotiator
                                    </div>
                                    <div className="text-[10px] text-silver-500 tracking-widest uppercase">AI coach · private</div>
                                </div>
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="text-silver-400 text-sm">Your {user?.role === "creator" ? "counter" : "offer"}:</span>
                                    <div className="flex items-center border border-white/10 rounded-sm overflow-hidden bg-ink-300">
                                        <span className="px-2 text-silver-500">$</span>
                                        <input
                                            type="number"
                                            value={coachOffer}
                                            onChange={(e) => { setCoachOffer(e.target.value); runCoach(e.target.value); }}
                                            placeholder="75000"
                                            className="bg-transparent py-2 pr-3 text-white placeholder:text-silver-500 focus:outline-none w-40 tabular-nums"
                                            data-testid="coach-offer-input"
                                        />
                                    </div>
                                    {coach.loading && <span className="text-[10px] text-silver-500 italic">analysing…</span>}
                                </div>
                                {coach.data && (
                                    <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start" data-testid="coach-verdict">
                                        <div>
                                            <div className={`text-sm font-medium mb-1 ${
                                                coach.data.verdict === "accept" ? "text-emerald-300" :
                                                coach.data.verdict === "counter_light" ? "text-platinum" :
                                                coach.data.verdict === "counter_medium" ? "text-amber-300" :
                                                "text-red-300"
                                            }`}>
                                                {coach.data.headline}
                                            </div>
                                            <div className="text-silver-400 text-xs leading-relaxed">{coach.data.coaching}</div>
                                            <div className="mt-2 text-[10px] tracking-widest uppercase text-silver-500">
                                                AI fair range: <span className="text-silver-300 tabular-nums">${coach.data.fair_range.low.toLocaleString()} – ${coach.data.fair_range.high.toLocaleString()}</span>
                                                {" · "}conf: {coach.data.confidence}
                                            </div>
                                        </div>
                                        {coach.data.suggested_counter_usd && (
                                            <button
                                                type="button"
                                                onClick={() => setCoachOffer(String(coach.data.suggested_counter_usd))}
                                                className="text-[10px] tracking-widest uppercase border border-platinum/40 text-platinum hover:bg-platinum/10 px-3 py-2 rounded-sm whitespace-nowrap"
                                                data-testid="coach-apply-counter"
                                            >
                                                Use ${Number(coach.data.suggested_counter_usd).toLocaleString()}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="px-5 py-4 border-t border-white/5 flex flex-wrap items-center gap-4 justify-between">
                            {deal.status === "initiated" && user?.role === "buyer" ? (
                                <>
                                    <div className="flex-1 min-w-[240px]">
                                        <label className="text-[10px] uppercase tracking-widest text-silver-400">
                                            Sign with your legal name
                                        </label>
                                        <input
                                            value={signatureName}
                                            onChange={(e) => setSignatureName(e.target.value)}
                                            className="signature-pad w-full bg-transparent border-b border-platinum/40 focus:border-platinum focus:outline-none py-1"
                                            placeholder="Your signature"
                                            data-testid="signature-input"
                                        />
                                    </div>
                                    <button
                                        onClick={sign}
                                        disabled={signing}
                                        className="inline-flex items-center gap-2 bg-platinum hover:bg-platinum-200 disabled:opacity-50 text-ink-300 px-5 py-3 rounded-sm uppercase tracking-widest text-xs font-medium"
                                        data-testid="sign-to-own-btn"
                                    >
                                        <PenLine className="w-4 h-4" />
                                        {signing ? "Signing…" : "Sign to Own"}
                                    </button>
                                </>
                            ) : deal.status === "signed" && user?.role === "buyer" ? (
                                <>
                                    <div className="text-sm text-silver-300">
                                        <div className="text-platinum tracking-widest uppercase text-[10px] mb-1">
                                            Signature recorded
                                        </div>
                                        Proceed to secured payment to complete transfer.
                                    </div>
                                    <button
                                        onClick={pay}
                                        disabled={paying}
                                        className="inline-flex items-center gap-2 bg-platinum hover:bg-platinum-200 disabled:opacity-50 text-ink-300 px-5 py-3 rounded-sm uppercase tracking-widest text-xs font-medium"
                                        data-testid="pay-now-btn"
                                    >
                                        <CreditCard className="w-4 h-4" />
                                        {paying ? "Redirecting…" : "Pay & Transfer"}
                                    </button>
                                </>
                            ) : deal.status === "transfer_complete" ? (
                                <div className="flex items-center justify-between w-full">
                                    <div className="text-emerald-300 text-sm">
                                        ✓ Ownership transferred. Vault unlocked.
                                    </div>
                                    <button
                                        onClick={() => nav(`/vault/${deal.id}`)}
                                        className="bg-platinum text-ink-300 px-4 py-2 rounded-sm uppercase tracking-widest text-xs"
                                        data-testid="go-to-vault-btn"
                                    >
                                        Open Vault
                                    </button>
                                </div>
                            ) : (
                                <div className="text-silver-400 text-sm">
                                    Contract awaiting buyer action.
                                </div>
                            )}
                        </div>
                    </motion.div>

                    {/* RIGHT: chat */}
                    <div className="glass rounded-sm flex flex-col max-h-[680px]">
                        <div className="px-5 py-3 border-b border-white/5 text-sm text-silver-300 flex items-center justify-between">
                            <span className="flex items-center gap-2">
                                Negotiation
                                <span
                                    className={`inline-block w-1.5 h-1.5 rounded-full ${wsStatus === "open" ? "bg-emerald-400 animate-pulse" : "bg-silver-500"}`}
                                    title={wsStatus === "open" ? "Live" : "Polling"}
                                    data-testid="ws-status-dot"
                                />
                                <span className="text-[10px] tracking-widest uppercase text-silver-500">
                                    {wsStatus === "open" ? "Live" : "Offline"}
                                </span>
                            </span>
                            <span className="text-xs text-silver-500 uppercase tracking-widest">
                                Secure channel
                            </span>
                        </div>
                        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="deal-chat">
                            {(deal.messages || []).map((m, i) => (
                                <div
                                    key={i}
                                    className={`max-w-[85%] px-4 py-2.5 rounded-sm text-sm ${
                                        m.from === "system"
                                            ? "mx-auto text-silver-400 text-xs italic bg-white/3"
                                            : m.from === user?.role
                                              ? "ml-auto bg-platinum/15 border border-platinum/20 text-white"
                                              : "mr-auto bg-ink-100 border border-white/5"
                                    }`}
                                >
                                    {m.from !== "system" && (
                                        <div className="text-[10px] tracking-widest uppercase text-silver-400 mb-0.5">
                                            {m.name || m.from}
                                        </div>
                                    )}
                                    {m.text}
                                </div>
                            ))}
                            {typingUser && typingUser !== user?.name && (
                                <div className="text-[11px] text-silver-500 italic pl-1" data-testid="typing-indicator">{typingUser} is typing…</div>
                            )}
                            <div ref={chatBottomRef} />
                        </div>
                        <form onSubmit={send} className="px-4 py-3 border-t border-white/5 flex items-center gap-2">
                            <input
                                value={msg}
                                onChange={(e) => onTyping(e.target.value)}
                                placeholder="Send a message…"
                                className="flex-1 bg-transparent border-b border-white/10 focus:border-platinum focus:outline-none py-2 text-sm"
                                data-testid="deal-chat-input"
                            />
                            <button
                                type="submit"
                                className="p-2 text-platinum hover:text-platinum-300"
                                data-testid="deal-chat-send"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* AI Clause Explainer Modal */}
            <AnimatePresence>
                {clause.open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={() => setClause({ open: false, text: "", loading: false, data: null })}
                        data-testid="clause-modal"
                    >
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 20, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-2xl bg-ink-200 border border-white/10 rounded-sm p-8 relative"
                        >
                            <button
                                onClick={() => setClause({ open: false, text: "", loading: false, data: null })}
                                className="absolute top-4 right-4 text-silver-500 hover:text-white"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <div className="flex items-center gap-2 mb-4 text-platinum">
                                <Sparkles className="w-4 h-4" />
                                <div className="text-xs tracking-[0.3em] uppercase">Clause explained · Claude Sonnet 4.5</div>
                            </div>
                            <div className="text-xs text-silver-500 mb-2 tracking-widest uppercase">Selection</div>
                            <div className="text-xs text-silver-400 italic mb-6 p-3 bg-ink-300 border border-white/5 rounded-sm max-h-32 overflow-y-auto">
                                "{clause.text}"
                            </div>
                            {clause.loading && <div className="text-silver-500 text-sm">Analysing…</div>}
                            {clause.data && (
                                <div className="space-y-5">
                                    <div>
                                        <div className="text-xs tracking-[0.3em] uppercase text-silver-400 mb-2">In plain English</div>
                                        <div className="text-silver-200 text-sm leading-relaxed">{clause.data.plain_english}</div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-xs tracking-[0.3em] uppercase text-silver-400">Risk</div>
                                        <span className={`px-2 py-1 text-[10px] tracking-widest uppercase rounded-sm ${
                                            clause.data.risk_level === "high" ? "bg-red-400/10 text-red-300 border border-red-400/30" :
                                            clause.data.risk_level === "medium" ? "bg-amber-300/10 text-amber-200 border border-amber-300/30" :
                                            "bg-emerald-300/10 text-emerald-200 border border-emerald-300/30"
                                        }`}>{clause.data.risk_level}</span>
                                        <span className="text-silver-400 text-xs">{clause.data.risk_notes}</span>
                                    </div>
                                    {clause.data.red_flags?.length > 0 && (
                                        <div>
                                            <div className="text-xs tracking-[0.3em] uppercase text-silver-400 mb-2">Red flags</div>
                                            <ul className="text-sm text-silver-300 space-y-1">
                                                {clause.data.red_flags.map((r, i) => <li key={i} className="flex gap-2"><span className="text-red-400">◆</span>{r}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
